/**
 * Locale-aware number & currency formatting.
 *
 * Arabic uses the ar-EG locale so numbers render with Arabic-Indic digits
 * (٠١٢٣٤٥٦٧٨٩); English falls back to en-US.
 */
import i18n from '../i18n/config';

const DEFAULT_CURRENCY = 'EGP';

/** Resolve the Intl locale from the active i18n language. */
export const getLocale = (): string =>
  i18n.language?.startsWith('ar') ? 'ar-EG' : 'en-US';

/** Format a plain number (thousands separators, no currency). */
export const formatNumber = (
  value: number | string | null | undefined,
  options?: Intl.NumberFormatOptions
): string => {
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (num === null || num === undefined || Number.isNaN(num)) {
    return '—';
  }
  return new Intl.NumberFormat(getLocale(), options).format(num);
};

/** Format a monetary amount with the currency symbol. */
export const formatCurrency = (
  value: number | string | null | undefined,
  currency = DEFAULT_CURRENCY
): string => {
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (num === null || num === undefined || Number.isNaN(num)) {
    return '—';
  }
  return new Intl.NumberFormat(getLocale(), {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(num);
};

/**
 * Format an already-scaled percentage value (e.g. 157.1) into a localized
 * percentage string: "157.1%" in English, "١٥٧٫١٪" in Arabic.
 */
export const formatPercent = (
  value: number | string | null | undefined,
  fractionDigits = 1
): string => {
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (num === null || num === undefined || Number.isNaN(num)) {
    return '—';
  }
  return new Intl.NumberFormat(getLocale(), {
    style: 'percent',
    minimumFractionDigits: 0,
    maximumFractionDigits: fractionDigits,
  }).format(num / 100);
};
